import { useAppDispatch, useAppSelector } from 'app/config/store';
import React, { useEffect, useState } from 'react';

import { Button, Col, Form, Input, InputNumber, Modal, Row, Select, Spin, Table, Tooltip } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import Title from 'antd/es/typography/Title';
import CommentComponent from 'app/entities/comment/comment';
import { getRankByPoint, getRanks } from 'app/entities/common/common.reducer';
import { formatPoint, percent } from 'app/shared/util/percent';
import dayjs from 'dayjs';
import * as _ from 'lodash';
import { Container } from 'reactstrap';
import { columnsResult, minusPoints, plusPoints } from '../config';
import { confirmComplete, getOtherComment, getOtherMinusComment, getOtherPlusComment, getSelfConfirmComplete, reset, saveDraft } from './confirm-complete.reducer';
import ModalConfirmComponent from 'app/shared/component/modal/confirm';
import { exportExcel } from 'app/entities/evaluation-level/evaluation-level.reducer';
import { saveAs } from 'file-saver';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import OtherCommentEvaluationComponent from './other-comment';
import OtherGeneralCommentComponent from '../other-comment';
import OtherPointsEvaluationComponent from './other-point';

type IConfirmCompleteEvaluationProps = {
    isOpen: boolean;
    onClose: (isReload?: boolean) => void;
    accountTableId: string,
    accountableName: string,
};

export const SelfConfirmCompleteEvaluation = ({ isOpen, onClose, accountTableId, accountableName }: IConfirmCompleteEvaluationProps) => {
    const dispatch = useAppDispatch();
    const [form] = Form.useForm();
    const updating = useAppSelector(state => state.selfConfirmCompleteEvaluation.updating);
    const loading = useAppSelector(state => state.selfConfirmCompleteEvaluation.loading);
    const entities = useAppSelector(state => state.selfConfirmCompleteEvaluation.entities);
    const updateSuccess = useAppSelector(state => state.selfConfirmCompleteEvaluation.updateSuccess);
    const ranks = useAppSelector(state => state.common.ranks);
    const account = useAppSelector(state => state.authentication.account);
    const currentPlanPeriod = useAppSelector(state => state.common.currentPlanPeriod);
    const otherComment = useAppSelector(state => state.selfConfirmCompleteEvaluation.otherComment);

    // Init state
    const [rankName, setRankName] = useState(null);
    const [isShowTable, setShowTable] = useState(false);
    const renderTotalMinusPoint = false
    const [isOpenDrawerComment, setOpenDrawerComment] = useState(false);
    const [isOpenModalConfirmComplete, setOpenModalConfirmComplete] = useState(false);
    const [currentIndexCriteria, setCurrentIndexCriteria] = useState(null);
    const [isLoadingComment, setLoadingComment] = useState(false);
    const [isOpenModalOtherComment, setOpenModalOtherComment] = useState(false);
    const [isOpenModalOtherGeneralComment, setOpenModalOtherGeneralComment] = useState(false);

    // form watch data
    const totalPlusPoints = Form.useWatch('totalPlusPoint', form)
    const totalMinusPoints = Form.useWatch('totalMinusPoint', form)

    useEffect(() => {
        if (accountTableId) innitData();
    }, [accountTableId]);

    useEffect(() => {
        if (updateSuccess) {
            handleClose(updateSuccess);
        }
    }, [updateSuccess]);

    useEffect(() => {
        form.setFieldsValue({
            plusPoint: entities?.selfEvaluation?.plusPoint ? mapPoints(entities?.selfEvaluation?.plusPoint) : plusPoints,
            minusPoint: entities?.selfEvaluation?.plusPoint ? mapPoints(entities?.selfEvaluation?.minusPoint) : minusPoints,
            totalPlusPoint: calculateTotalWithFormat(entities?.selfEvaluation?.plusPoint),
            totalMinusPoint: calculateTotalWithFormat(entities?.selfEvaluation?.minusPoint),
            userRankingEvaluateId: entities?.selfEvaluation?.userRankingEvaluateId,
            generalFeedback: entities?.selfEvaluation?.generalFeedback,
        });
    }, [entities]);

    useEffect(() => {
        if (entities) {
            const totalResultPoints = entities.krYearStatistic?.checkinPoint + percent(totalPlusPoints) - percent(totalMinusPoints)
            if (_.isNumber(totalResultPoints)) {
                handleGetRankByPoint(totalResultPoints);
            }
            form.setFieldsValue({ totalResultPoint: formatPoint(totalResultPoints) })
        }
    }, [totalPlusPoints, totalMinusPoints, entities]);

    const mapPoints = (points) =>
        points?.map((x) => ({
            ...x,
            point: x.point && formatPoint(x.point),
        }));

    const calculateTotalWithFormat = (points) =>
        formatPoint(_.sumBy(points, (o: any) => o.point));

    const innitData = () => {
        dispatch(getRanks({ partyId: accountTableId, year: currentPlanPeriod?.label, }))
        dispatch(getSelfConfirmComplete({ ownerId: accountTableId, planPeriodId: currentPlanPeriod?.value, userId: account?.id }));
    };

    const mapperDataPayload = (values) => {
        const payload = {
            ...values,
            ownerId: accountTableId,
            userId: account?.id,
            planPeriodId: currentPlanPeriod?.value,
            checkinPoint: entities?.krYearStatistic?.checkinPoint,
            year: currentPlanPeriod?.label,
            documentId: entities?.documentId,
            plusPoint: values?.plusPoint?.map(x => (
                { ...x, point: x.point ? percent(x.point) : null }
            )),
            minusPoint: values?.minusPoint?.map(x => (
                { ...x, point: x.point ? percent(x.point) : null }
            )),
        };
        return payload;
    }

    const handleSubmit = () => {
        handleOpenModalConfirmComplete()
    };

    const handleOpenModalConfirmComplete = () => {
        setOpenModalConfirmComplete(true);
    }

    const handleConfirmComplete = () => {
        setOpenModalConfirmComplete(false);
        form.validateFields().then(values => {
            const payload = mapperDataPayload(values);
            dispatch(confirmComplete(payload))

        }).catch(() => { return; });
    }

    const handleSaveDraft = () => {
        form.validateFields().then(values => {
            const payload = mapperDataPayload(values);
            dispatch(saveDraft(payload)).then((res) => {
                if (res && res.payload) {
                    handleClose()
                }
            });
        }).catch(() => { return; });
    };

    const handleClose = (reload = false) => {
        onClose(reload);
        dispatch(reset())
    };

    // Handle changes in form values to update the total points
    const handleValuesChange = (__, allValues) => {
        const calculateTotal = (points) =>
            points?.reduce((sum, item) => sum + (item.point || 0), 0) || 0;

        form.setFieldsValue({
            totalPlusPoint: calculateTotal(allValues.plusPoint),
            totalMinusPoint: calculateTotal(allValues.minusPoint),
        });
    };

    const handleGetRankByPoint = (point) => {
        if (point) {
            dispatch(getRankByPoint({ partyId: accountTableId, year: currentPlanPeriod?.label, point })).then((res) => {
                if (res && res.payload) {
                    const result = (res.payload as any).data
                    setRankName(`${result?.code} - ${result?.name}`)
                }
            });
        }
    }

    const handleShowDrawerComment = () => {
        setOpenDrawerComment(true);
    };

    const handleCloseDrawerComment = () => {
        setOpenDrawerComment(false);
    };

    const handleExport = () => {
        dispatch(
            exportExcel({ ownerId: accountTableId, reportScheduleId: currentPlanPeriod?.value })).then(res => {
                if (res.payload) {
                    const resultExcel = res.payload as any;
                    saveAs(
                        new Blob([resultExcel.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
                        `Kết quả_${dayjs().format('DDMMYYYYhhmmss')}`,
                    );
                }
            });
    }

    const handleOpenModalOtherComment = () => {
        setOpenModalOtherComment(true);
    }

    const handleCloseModalOtherComment = () => {
        setOpenModalOtherComment(false);
    }

    const handleGetOtherPlusComments = (index: number) => {
        setCurrentIndexCriteria(index);
        handleOpenModalOtherComment()
        dispatch(getOtherPlusComment({ index, ownerId: accountTableId, documentId: entities?.documentId, evaluateLevelId: entities?.evaluateLevelId, planPeriodId: currentPlanPeriod?.value }))
            .finally(() => { setLoadingComment(false) });
    }

    const handleGetOtherMinusComments = (index: number) => {
        setCurrentIndexCriteria(index);
        handleOpenModalOtherComment()
        dispatch(getOtherMinusComment({ index, ownerId: accountTableId, documentId: entities?.documentId, evaluateLevelId: entities?.evaluateLevelId, planPeriodId: currentPlanPeriod?.value }))
            .finally(() => { setLoadingComment(false) });
    }

    const handleOpenModalOtherGeneralComment = () => {
        setOpenModalOtherGeneralComment(true);
    }

    const handleCloseModalOtherGeneralComment = () => {
        setOpenModalOtherGeneralComment(false);
    }

    const handleGetOtherComments = () => {
        handleOpenModalOtherGeneralComment()
        dispatch(getOtherComment({ ownerId: accountTableId, documentId: entities?.documentId, evaluateLevelId: entities?.evaluateLevelId, planPeriodId: currentPlanPeriod?.value }))
            .finally(() => { setLoadingComment(false) });
    }

    return (
        <Modal
            width={1150}
            open={isOpen}
            footer={null}
            onCancel={() => {
                handleClose();
            }}
            maskClosable={false}
        >
            <Container className="form">
                <Spin spinning={loading || updating} delay={300}>
                    <Form initialValues={{
                        plusPoint: plusPoints,
                        minusPoint: minusPoints
                    }} size="large" form={form}
                        onValuesChange={handleValuesChange} onFinish={handleSubmit} layout="vertical">
                        <Row>
                            <Col md={20}>
                                <Title level={3}>{`Xác nhận hoàn thành tự đánh giá ${accountableName ? `- ${accountableName}` : ''}`}</Title>
                            </Col>
                            <Col md={1} />
                            <Col md={3}><Button onClick={handleShowDrawerComment}>Ý kiến trao đổi</Button></Col>
                        </Row>
                        <Row>
                            <Col md={3}>
                                <Form.Item label="Kỳ báo cáo">
                                    <Input disabled value={currentPlanPeriod?.label} />
                                </Form.Item>
                            </Col>
                            <Col md={18} />
                            {/* <Col md={3}><Button className='green' onClick={handleExport}>Xuất Excel</Button></Col> */}
                        </Row>
                        <Row className='my-3'>
                            <Col md={11}>
                                <span className="fw-bold h5">I. ĐIỂM ĐÁNH GIÁ HIỆN MỤC TIÊU</span>
                            </Col>
                            <Col md={1} />
                            <Col md={3}>
                                <Form.Item>
                                    <InputNumber value={formatPoint(entities?.krYearStatistic?.checkinPoint)}
                                        disabled className='w-100' addonAfter="%" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row className='my-3 ps-3'>
                            <Col md={24}>
                                <span className="fw-bold h6 cursor-pointer" onClick={() => { setShowTable(!isShowTable) }} >Xem thống kế số lượng theo tình trạng thực hiện</span>
                            </Col>
                        </Row>
                        {isShowTable &&
                            <Row className='ps-3'>
                                <Col md={24}>
                                    <Table
                                        pagination={false}
                                        bordered
                                        dataSource={entities?.krYearStatistic?.krStatistics.map(x => ({ ...x, key: x.name }))}
                                        columns={columnsResult}
                                        className="w-100"
                                        rowClassName={(record, index) => {
                                            return index === entities?.krYearStatistic?.krStatistics.length - 1 ? 'fw-bold' : '';
                                        }}
                                    />
                                </Col>
                            </Row>
                        }
                        <Row className='my-3 ps-2'>
                            <Col md={11}>
                                <span className="fw-bold h6">ĐIỂM CỘNG</span>
                            </Col>
                            <Col md={1} />
                            <Col md={3}>
                                <Form.Item
                                    name={'totalPlusPoint'}
                                    rules={[{ max: 5, type: 'integer', message: 'Tổng điểm cộng không được lớn hơn 5%' }]}
                                >
                                    <InputNumber disabled className='w-100' addonAfter="%" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.List name="plusPoint">
                            {(fields) => (
                                <>
                                    {fields.map(({ key, name, ...restField }) => (
                                        <Row key={key} className='my-3 ps-3'>
                                            <Col md={11}>
                                                <span className="fw-bold">{`${name + 1}. ${plusPoints[name].name}`}</span>
                                                <span className='ps-3'>
                                                    <Tooltip title="Xem ý kiến khác">
                                                        <FontAwesomeIcon
                                                            onClick={() => {
                                                                handleGetOtherPlusComments(name);
                                                            }}
                                                            icon="eye"
                                                            color='blue'
                                                            className='cursor-pointer'
                                                        />
                                                    </Tooltip>
                                                </span>
                                            </Col>
                                            <Col md={1} />
                                            <Col md={3}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'point']}
                                                >
                                                    <InputNumber onChange={() => { form.validateFields([['plusPoint', name, 'explain'], 'totalPlusPoint']) }} className='w-100' min={1} addonAfter="%" />
                                                </Form.Item>
                                            </Col>
                                            <Col md={1} />
                                            <Col md={8}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'explain']}
                                                    rules={[
                                                        ({ getFieldValue }) => ({
                                                            validator(__, value) {
                                                                const pointValue = getFieldValue(['plusPoint', name, 'point']);
                                                                if (pointValue > 0 && !value) {
                                                                    return Promise.reject(new Error('VUi lòng nhập'));
                                                                }
                                                                return Promise.resolve();
                                                            },
                                                        }),
                                                    ]}>
                                                    <TextArea placeholder='Điền diễn giải' className='w-100' rows={1} />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    ))}
                                </>
                            )}
                        </Form.List>
                        <Row className='my-3 ps-2'>
                            <Col md={11}>
                                <span className="fw-bold h6">ĐIỂM TRỪ</span>
                            </Col>
                            <Col md={1} />
                            <Col md={3}>
                                <Form.Item
                                    name={'totalMinusPoint'}
                                >
                                    {renderTotalMinusPoint && <InputNumber disabled className='w-100' addonAfter="%" />}
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.List name="minusPoint">
                            {(fields) => (
                                <>
                                    {fields.map(({ key, name, ...restField }) => (
                                        <Row key={key} className='my-3 ps-3'>
                                            <Col md={11}>
                                                <span className="fw-bold">{`${name + 1}. ${minusPoints[name].name}`}</span>
                                                <span className='ps-3'>
                                                    <Tooltip title="Xem ý kiến khác">
                                                        <FontAwesomeIcon
                                                            onClick={() => {
                                                                handleGetOtherMinusComments(name);
                                                            }}
                                                            icon="eye"
                                                            color='blue'
                                                            className='cursor-pointer'
                                                        />
                                                    </Tooltip>
                                                </span>
                                            </Col>
                                            <Col md={1} />
                                            <Col md={3}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'point']}>
                                                    <InputNumber onChange={() => { form.validateFields([['minusPoint', name, 'explain'], 'totalMinusPoint']) }} className='w-100' min={1} addonAfter="%" />
                                                </Form.Item>
                                            </Col>
                                            <Col md={1} />
                                            <Col md={8}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'explain']}
                                                    rules={[
                                                        ({ getFieldValue }) => ({
                                                            validator(__, value) {
                                                                const pointValue = getFieldValue(['minusPoint', name, 'point']);
                                                                if (pointValue > 0 && !value) {
                                                                    return Promise.reject(new Error('VUi lòng nhập'));
                                                                }
                                                                return Promise.resolve();
                                                            },
                                                        }),
                                                    ]}>
                                                    <TextArea placeholder='Điền diễn giải' className='w-100' rows={1} />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    ))}
                                </>
                            )}
                        </Form.List>
                        <hr />
                        <Row className='my-3'>
                            <Col md={24}>
                                <span className="fw-bold h5">II. TỔNG HỢP KẾT QUẢ ĐÁNH GIÁ</span>
                            </Col>
                        </Row>
                        <Row className='my-3 ps-2'>
                            <Col md={11}>
                                <span className="fw-bold h6">1. TỔNG ĐIỂM ĐÁNH GIÁ</span>
                            </Col>
                            <Col md={1} />
                            <Col md={3}>
                                <Form.Item
                                    name={'totalResultPoint'}
                                >
                                    <InputNumber disabled className='w-100' addonAfter="%" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row className='my-3 ps-2'>
                            <Col md={11}>
                                <span className="fw-bold h6">2. KẾT QUẢ XẾP LOẠI THEO TỔNG ĐIỂM</span>
                            </Col>
                            <Col md={1} />
                            <Col md={6}>
                                <Form.Item
                                >
                                    <Input value={rankName} disabled className='w-100' />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row className='my-3 ps-2'>
                            <Col md={11}>
                                <span className="fw-bold h6">3. KẾT QUẢ XẾP LOẠI CUỐI CÙNG <span className='text-danger mb-2'>*</span></span>
                            </Col>
                            <Col md={1} />
                            <Col md={6}>
                                <Form.Item
                                    name={'userRankingEvaluateId'}
                                    rules={[{ required: true, message: 'Vui lòng chọn' }]}
                                >
                                    <Select options={ranks?.map(x => ({
                                        label: x.name,
                                        value: x.id,
                                    }))} className='w-100' />
                                </Form.Item>
                            </Col>
                        </Row>
                        <hr />
                        <Row className='my-3'>
                            <Col md={24}>
                                <span className="fw-bold h5">III. Ý KIẾN CHUNG</span>
                                <span className='ps-2'>
                                    <Tooltip title="Xem ý kiến khác">
                                        <FontAwesomeIcon
                                            onClick={() => {
                                                handleGetOtherComments();
                                            }}
                                            icon="eye"
                                            color='blue'
                                            className='cursor-pointer'
                                        />
                                    </Tooltip>
                                </span>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={24}>
                                <Form.Item name={'generalFeedback'}>
                                    <TextArea className='w-100' rows={4} />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.Item>
                            <Row className="action" justify="end">
                                <Col>
                                    <Button
                                        onClick={() => {
                                            handleClose();
                                        }}
                                    >
                                        Hủy
                                    </Button>
                                </Col>
                                <Col>
                                    <Button
                                        className="green"
                                        onClick={() => {
                                            handleSaveDraft();
                                        }}
                                        disabled={updating}
                                    >
                                        Lưu nháp
                                    </Button>
                                </Col>
                                <Col>
                                    <Button type="primary" htmlType="submit" disabled={updating}>
                                        Hoàn thành tự đánh giá và gửi cấp tiếp theo
                                    </Button>
                                </Col>
                            </Row>
                        </Form.Item>
                    </Form>
                    {isOpenDrawerComment &&
                        <CommentComponent topicId={entities?.documentId} handleDrawerComment={handleCloseDrawerComment} isOpenDrawerComment={isOpenDrawerComment} />
                    }
                    {isOpenModalOtherComment && <OtherCommentEvaluationComponent
                        data={otherComment}
                        isLoading={isLoadingComment}
                        nameCriteria={plusPoints[currentIndexCriteria].name}
                        isOpen={isOpenModalOtherComment} onClose={handleCloseModalOtherComment} />}

                    {isOpenModalOtherGeneralComment && <OtherGeneralCommentComponent
                        data={otherComment}
                        isLoading={isLoadingComment}
                        isOpen={isOpenModalOtherGeneralComment} onClose={handleCloseModalOtherGeneralComment} />}
                    <ModalConfirmComponent
                        visible={isOpenModalConfirmComplete}
                        onCancel={() => {
                            setOpenModalConfirmComplete(false);
                        }}
                        onConfirm={handleConfirmComplete}
                        isLoading={updating}
                        title={`Bạn đã hoàn thành tự đánh giá?`}
                    />
                </Spin>
            </Container>
        </Modal>
    );
};

export default SelfConfirmCompleteEvaluation;
